import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

export const LoginPage: React.FC = () => {
  const { login, entraLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      // AuthContext flips the app into the authenticated view
    } catch (err: any) {
      setError(err?.message || 'Login failed');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 font-sans antialiased">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Meridian Logo" className="h-40 w-auto object-contain mx-auto" />
          <h1 className="mt-5 text-2xl font-bold text-slate-800 tracking-tight">Meridian</h1>
          <p className="mt-1.5 text-sm font-semibold text-mantis-600">
            An Agentic Healthcare Operations Platform
          </p>
          <p className="mt-2 text-xs leading-relaxed text-slate-500 max-w-xs mx-auto">
            Workstreams enhanced by AI Agents with HITL (Human-in-the-loop) optimizing your full potential
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-4"
        >
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5" htmlFor="email">
              Work Email
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-mantis-400 focus:border-mantis-400"
              placeholder="you@company.com"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-mantis-400 focus:border-mantis-400"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div className="text-xs text-redwood-600 bg-redwood-50 border border-redwood-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full py-2.5 rounded-lg bg-mantis-500 text-white text-sm font-semibold hover:bg-mantis-600 transition-colors disabled:opacity-60"
          >
            {busy ? 'Signing in…' : 'Sign In'}
          </button>

          <div className="flex items-center gap-3 pt-2">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await entraLogin();
              } catch (err: any) {
                setError(err?.message || 'Microsoft sign-in failed');
                setBusy(false);
              }
            }}
            className="w-full py-2.5 rounded-lg bg-white border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Sign in with Microsoft
          </button>
        </form>

        <div className="mt-5 bg-mantis-50 border border-mantis-200 rounded-xl px-4 py-3 text-center">
          <p className="text-xs font-semibold text-slate-700">New to Meridian?</p>
          <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
            Request a demo login for your business at{' '}
            <a
              href="mailto:support@meridianhealthops.com"
              className="font-semibold text-mantis-600 hover:text-mantis-700 underline underline-offset-2"
            >
              support@meridianhealthops.com
            </a>
          </p>
        </div>

        <p className="mt-6 text-center text-[10px] text-slate-400">
          Authorized users only. Access is scoped to your assigned businesses.
        </p>
      </div>
    </div>
  );
};
